// partenaires.ts - restaurants affichés dans le carrousel « Les restaurants
// déjà partenaires » de l'accueil (docs/rules/vitrine-partenaires-landing.md).
//
// Lecture PUBLIQUE (`restos` et `villes` sont lisibles par tous), Firestore
// chargé paresseusement comme pour les villes. Ne lève JAMAIS : la vitrine est
// un ornement, un échec réseau ne doit pas casser la page d'accueil - on
// retombe sur une liste vide et la section entière disparaît.
import { COLLECTIONS, type Resto, type Ville } from "@hop-resto/shared";
import { getDb } from "./firebase";

/** Un partenaire tel que l'affiche le carrousel : logo, nom, ville. */
export interface Partenaire {
  id: string;
  nom: string;
  /** Nom court de la ville ("Compiègne et environs" -> "Compiègne"), peut être vide. */
  ville: string;
  /** URL du logo, éventuellement vide - le carrousel affiche alors un monogramme. */
  logoUrl: string;
  /**
   * Lien vers la page publique de disponibilités du resto (`/r/{id}`, servie
   * par CETTE target), ou chaîne VIDE si le resto n'en a pas.
   *
   * La page n'existe que si `modules.widget === true` : sans lui, `/r/{id}`
   * répond « Pas encore de page ici » (docs/specs/spec-widget-dispos.md).
   * On ne renvoie donc jamais le visiteur vers une impasse - la vignette
   * reste alors simplement non cliquable.
   */
  pageUrl: string;
}

/** "Compiègne et environs" -> "Compiègne" (même règle que villes.ts). */
function shortName(nom: string): string {
  return nom.split(" ")[0] ?? nom;
}

/**
 * Restaurants ACTIFS dont l'admin a coché la vitrine landing
 * (`modules.vitrine_landing`), avec le nom court de leur ville.
 *
 * Deux filtres d'ÉGALITÉ seulement (`actif` + `modules.vitrine_landing`) :
 * aucun index composite requis, comme les requêtes des Cloud Functions.
 * Ne rejette jamais - `[]` en cas d'échec.
 */
export async function fetchPartenaires(): Promise<Partenaire[]> {
  try {
    const [{ collection, getDocs, query, where }, db] = await Promise.all([
      import("firebase/firestore"),
      getDb(),
    ]);

    const snap = await getDocs(
      query(
        collection(db, COLLECTIONS.restos),
        where("actif", "==", true),
        where("modules.vitrine_landing", "==", true),
      ),
    );
    if (snap.empty) return [];

    // Les villes ne sont lues QUE si au moins un partenaire existe, et une
    // seule fois pour tout le carrousel (une lecture par resto serait
    // inutilement bavarde - docs/architecture/couts-firebase.md).
    const villes = new Map<string, string>();
    try {
      const villesSnap = await getDocs(collection(db, COLLECTIONS.villes));
      for (const d of villesSnap.docs) {
        const nom = (d.data() as Ville).nom;
        if (typeof nom === "string" && nom.length > 0) villes.set(d.id, shortName(nom));
      }
    } catch (err) {
      // Ville introuvable = carte sans sous-titre, jamais une vitrine vide.
      console.error("[landing] failed to load villes for partenaires", err);
    }

    return snap.docs
      .map((d) => {
        const r = d.data() as Resto;
        // Même résolution du logo que la PWA et le widget : la surcharge du
        // restaurateur (parametres.logo_url) prime sur la fiche admin.
        const logoUrl = (r.parametres?.logo_url ?? "").trim() || (r.logo_url ?? "").trim();
        return {
          id: d.id,
          nom: typeof r.nom === "string" ? r.nom.trim() : "",
          ville: villes.get(r.ville_id) ?? "",
          logoUrl,
          // Lien SEULEMENT si la page existe vraiment (module widget actif).
          pageUrl: r.modules?.widget === true ? `/r/${d.id}` : "",
        };
      })
      .filter((p): p is Partenaire => p.nom.length > 0)
      .sort((a, b) => a.nom.localeCompare(b.nom, "fr"));
  } catch (err) {
    console.error("[landing] failed to load partenaires", err);
    return [];
  }
}
